/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { apiClient } from '@/lib/api-client'
import { logger } from '@/lib/logger'
import { authService } from '../services/auth.service'
import { identifyUser, resetUser } from '@/lib/posthog'
import type { User, Organization, LoginRequest, RegisterRequest } from '../types/auth.types'

function identifyPostHogUser(user: User, organization?: Organization | null) {
  identifyUser(user.id, {
    email: user.email,
    name: user.full_name,
    role: user.role,
    organization_id: user.organization_id,
    organization_name: organization?.name,
  })
}

interface AuthContextType {
  user: User | null
  organization: Organization | null
  loading: boolean
  isAuthenticated: boolean
  signIn: (credentials: LoginRequest) => Promise<void>
  signOut: () => Promise<void>
  register: (data: RegisterRequest) => Promise<void>
  refetchUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

/**
 * Auth Provider Component
 *
 * Provides global authentication state to the app
 * - Loads user on mount if tokens exist
 * - Exposes signIn, signOut, register methods
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [loading, setLoading] = useState(true)

  /**
   * Load user profile if authenticated
   * Uses checkProfile to avoid 401 errors during OAuth onboarding flow
   */
  const loadUser = useCallback(async () => {
    if (!apiClient.isAuthenticated()) {
      setLoading(false)
      return
    }

    try {
      // Check if user has org first to avoid 401 during onboarding
      const profileCheck = await authService.checkProfile()

      if (!profileCheck.has_org) {
        // User authenticated but no org yet (OAuth onboarding flow)
        logger.debug('AuthContext: User has no org yet, skipping full profile load')
        setLoading(false)
        return
      }

      // User has org, safe to load full profile
      const { user, organization } = await authService.getProfile()
      setUser(user)
      setOrganization(organization)
      identifyPostHogUser(user, organization)
    } catch (error) {
      logger.error('Failed to load user:', error)
      // Clear invalid tokens
      apiClient.clearTokens()
      setUser(null)
      setOrganization(null)
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Initial load on mount
   */
  useEffect(() => {
    loadUser()
  }, [loadUser])

  /**
   * Sign in with email/password
   */
  const signIn = useCallback(async (credentials: LoginRequest) => {
    setLoading(true)
    try {
      logger.debug('AuthContext: Calling authService.login...')
      const response = await authService.login(credentials)
      logger.debug('AuthContext: Login response received:', {
        hasUser: !!response.user,
        hasOrg: !!response.organization,
        user: response.user
      })
      setUser(response.user)
      setOrganization(response.organization)
      identifyPostHogUser(response.user, response.organization)
      logger.debug('AuthContext: User and organization state updated')
    } catch (error) {
      logger.error('AuthContext: Login failed:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Sign out
   */
  const signOut = useCallback(async () => {
    setLoading(true)
    try {
      await authService.logout()
    } finally {
      setUser(null)
      setOrganization(null)
      resetUser()
      setLoading(false)
      // Clear session greeting flag so it shows again on next login
      sessionStorage.removeItem('arceusx_session_greeting_shown')
    }
  }, [])

  /**
   * Register new organization
   */
  const register = useCallback(async (data: RegisterRequest) => {
    setLoading(true)
    try {
      const response = await authService.register(data)
      setUser(response.user)
      setOrganization(response.organization)
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Refetch user profile
   */
  const refetchUser = useCallback(async () => {
    await loadUser()
  }, [loadUser])

  const isAuthenticated = !!user && apiClient.isAuthenticated()

  const value: AuthContextType = {
    user,
    organization,
    loading,
    isAuthenticated,
    signIn,
    signOut,
    register,
    refetchUser,
  }

  // Debug logging
  logger.debug('AuthContext state:', {
    hasUser: !!user,
    userEmail: user?.email,
    apiClientAuth: apiClient.isAuthenticated(),
    isAuthenticated,
    loading
  })

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * useAuth Hook
 *
 * Access auth context in any component
 */
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
